import { userStore } from '@/models/stores';
import type { ConsolidatedCustomer } from '@/types/leads';
import { CircleSmall, Sparkle } from 'lucide-react';
import { Badge } from '../badge';
import PaddockAvatar from '../paddock-avatar/paddock-avatar';

const LeadLine = ({
  lead,
  onClick,
}: {
  lead: ConsolidatedCustomer;
  onClick: () => void;
}) => {
  const name =
    lead.personal_details?.first_name == lead.personal_details?.last_name
      ? lead.personal_details?.first_name
      : lead.personal_details?.first_name +
        ' ' +
        lead.personal_details?.last_name;

  const salesRep = userStore.getById(
    lead.original_sales_rep_id ?? 'ad0f1919-2dda-4616-9bfb-81bfd4fc792f',
  );

  return (
    <div>
      <a
        className="flex flex-row items-center gap-2 px-4 py-2 border-b hover:bg-gray-100 justify-between"
        onClick={onClick}
      >
        <div className="flex flex-row items-center gap-2">
          <div className="w-46 text-nowrap overflow-hidden text-ellipsis">
            {name}
          </div>
          <div className="text-nowrap overflow-hidden text-ellipsis">
            <LeadPriorityScoreBadge priority_score={lead.priority} />
          </div>
          {lead.has_ever_account && (
            <div className="text-nowrap overflow-hidden text-ellipsis">
              <HasEverAccountBadge
                has_ever_account={lead.has_ever_account}
                has_had_reservation={
                  lead.previous_reservations &&
                  lead.previous_reservations.length > 0
                }
              />
            </div>
          )}
        </div>
        <div className="flex flex-row items-center gap-6">
          {salesRep && (
            <div className="text-nowrap overflow-hidden text-ellipsis">
              {salesRep.username}
            </div>
          )}
          {salesRep && (
            <PaddockAvatar
              src={salesRep.avatar}
              alt={`${salesRep.first_name} ${salesRep.last_name}`}
              fallback={`${salesRep.first_name.charAt(0)} ${salesRep.last_name.charAt(0)}`}
              className="size-5 rounded-full"
            />
          )}
        </div>
      </a>
    </div>
  );
};

export default LeadLine;

export type LeadPriorityScoreBadge = {
  priority_score: number;
};

export const LeadPriorityScoreBadge = ({
  priority_score,
}: LeadPriorityScoreBadge) => {
  let copy = '';
  let color = '';

  if (priority_score >= 20) {
    copy = 'High';
    color = 'var(--color-red-400)';
  } else if (priority_score >= 10) {
    copy = 'Medium';
    color = 'var(--color-orange-400)';
  } else if (priority_score >= 0) {
    copy = 'Low';
    color = 'var(--color-green-400)';
  } else {
    copy = 'Low';
  }

  return (
    <Badge className="text-black bg-white  text-xs shadow-none rounded-full border-foreground/10 pointer-events-none pl-2 pr-2">
      {priority_score}
      <CircleSmall fill={color} stroke={color} className="size-4 mr-1 ml-1" />
      {copy}
    </Badge>
  );
};

export const HasEverAccountBadge = ({
  has_ever_account,
  has_had_reservation,
}: {
  has_ever_account: boolean;
  has_had_reservation?: boolean | undefined;
}) => {
  if (!has_ever_account) {
    return null;
  }

  return (
    <Badge className="text-black bg-white  text-xs shadow-none rounded-full border-foreground/10 pointer-events-none pl-2 pr-2">
      <Sparkle
        fill="var(--color-indigo-400)"
        stroke="var(--color-indigo-400)"
        className="size-4 mr-1 ml-1"
      />
      Account {has_had_reservation && '& prev reservation'}
    </Badge>
  );
};
